import type { ReactNode } from 'react';
import { MotionConfig } from 'motion/react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useMe } from './lib/queries';
import { useTheme } from './lib/theme';
import { Shell } from './shell/Shell';
import { Login } from './pages/Login';
import { SetPassword } from './pages/SetPassword';
import { Today } from './pages/Today';
import { CalendarPage } from './pages/Calendar';
import { RemindersPage } from './pages/Reminders';
import { AssistantPage } from './pages/Assistant';
import { RequestsPage } from './pages/Requests';
import { NotificationsPage } from './pages/Notifications';
import { SpacePage } from './pages/Space';
import { TrackPage } from './pages/Track';
import { ActivityPage } from './pages/Activity';
import { CheckPage } from './pages/Check';
import { SettingsPage } from './pages/Settings';
import { KnowledgePage } from './pages/Knowledge';
import { FileRoute, FolderPage, KnowledgeHome } from './pages/KnowledgeBase';
import { SearchPage } from './pages/Search';
import { MeetingsPage } from './pages/Meetings';
import { RecordPage } from './pages/Record';
import { UsagePage } from './pages/Usage';
import { Loading } from './ui/bits';
import { ToastProvider } from './ui/toast';
import { usePhone } from './lib/media';
import { PhoneSpaces } from './phone/Spaces';
import { PhoneMore } from './phone/More';

export function App() {
  const { theme, toggle } = useTheme();
  const loc = useLocation();
  const phone = usePhone();
  const me = useMe(loc.pathname !== '/login' && loc.pathname !== '/set-password');
  const root = `c ${theme}`;
  // Toasts render inside the themed root so they get the light or dark colours and the app font.
  // reducedMotion "user": with "reduce motion" set in the OS, transforms and layout animations are off and
  // only opacity animates, so every movement becomes a fade.
  const themed = (children: ReactNode) => <MotionConfig reducedMotion="user"><div className={root}><ToastProvider>{children}</ToastProvider></div></MotionConfig>;
  if (loc.pathname === '/login') return themed(<Login theme={theme} onTheme={toggle} />);
  if (loc.pathname === '/set-password') return themed(<SetPassword theme={theme} onTheme={toggle} />);
  if (me.isLoading) return themed(<Loading />);
  if (!me.data) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
  return themed(
    <Shell me={me.data} theme={theme} onTheme={toggle}>
      <Routes>
        <Route path="/" element={<Today me={me.data} />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/reminders" element={<RemindersPage me={me.data} />} />
        <Route path="/assistant" element={<AssistantPage />} />
        <Route path="/assistant/waiting" element={<AssistantPage waitingView />} />
        {/* Talk it through is voice mode in the chat now (Kareem, 4 Nov); old links land there. */}
        <Route path="/assistant/talk" element={<Navigate to="/assistant?voice=1" replace />} />
        <Route path="/assistant/:id" element={<AssistantPage />} />
        <Route path="/requests" element={<RequestsPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        {/* Spaces and More are the phone's tabs; wider screens have them in the sidebar. */}
        <Route path="/spaces" element={phone ? <PhoneSpaces /> : <Navigate to="/" replace />} />
        <Route path="/more" element={phone ? <PhoneMore me={me.data} theme={theme} onTheme={toggle} /> : <Navigate to="/" replace />} />
        <Route path="/spaces/:id" element={<SpacePage me={me.data} />} />
        <Route path="/tracks/:id" element={<TrackPage me={me.data} />} />
        <Route path="/activities/:id" element={<ActivityPage me={me.data} />} />
        <Route path="/checks/:id" element={<CheckPage me={me.data} />} />
        <Route path="/settings" element={<SettingsPage me={me.data} />} />
        <Route path="/settings/usage" element={<UsagePage me={me.data} />} />
        <Route path="/knowledge" element={<KnowledgePage />} />
        <Route path="/folders" element={<KnowledgeHome me={me.data} />} />
        <Route path="/folders/:id" element={<FolderPage me={me.data} />} />
        <Route path="/files/:id" element={<FileRoute me={me.data} />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/meetings" element={<MeetingsPage />} />
        <Route path="/meetings/:id" element={<MeetingsPage />} />
        <Route path="/record" element={<RecordPage />} />
        <Route path="*" element={<div className="empty">This page does not exist.</div>} />
      </Routes>
    </Shell>,
  );
}
